import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import EmptyCard from '@components/EmptyCard/EmptyCard';
import Loadable from '@components/Loadable/Loadable';
import { InquiryEntryType } from '@interfaces/inquiries';
import {
  handleGetAdminMedicationDetails,
  handleGetAdminSupplementDetails,
} from '@store/features/adminInquiries/inquiryEntryDetails/actions';
import {
  adminActiveInquiryEntryTypeSelector,
  adminInquiryEntryDetailsActiveIdSelector,
  adminInquiryEntryDetailsErrorSelector,
  adminInquiryEntryDetailsIsLoadingSelector,
} from '@store/features/adminInquiries/inquiryEntryDetails/selectors';
import useDispatch from '@store/hooks/useDispatch';
import MedicationInquiryDetails from '@views/AdminInquiriesPage/AdminInquiryList/MedicationInquiryDetails/MedicationInquiryDetails';

import SupplementInquiryDetails from '../SupplementInquiryDetails/SupplementInquiryDetails';

import './style.scss';

interface IProps {}

const AsideFormCard: FC<IProps> = (props: IProps): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const activeEntryId = useSelector(adminInquiryEntryDetailsActiveIdSelector);
  const activeEntryType = useSelector(adminActiveInquiryEntryTypeSelector);
  const isEntryDetailsLoading = useSelector(adminInquiryEntryDetailsIsLoadingSelector);
  const entryDetailsError = useSelector(adminInquiryEntryDetailsErrorSelector);

  const handleRetryFetchInquiryEntryDetails = useCallback(() => {
    if (!activeEntryId) {
      return;
    }
    if (activeEntryType === InquiryEntryType.Supplement) {
      dispatch(handleGetAdminSupplementDetails(activeEntryId));
    }
    if (activeEntryType === InquiryEntryType.Supplement) {
      dispatch(handleGetAdminMedicationDetails(activeEntryId));
    }
  }, [dispatch, activeEntryId]);

  if (!activeEntryId) {
    return (
      <EmptyCard
        className="admin-inquiry-list__empty-card"
        borderType="dashed"
        icon="/images/img-dietarysupplement.svg"
        description={t('adminInquiries.selectToReview')}
      />
    );
  }

  return (
    <Loadable
      isLoading={isEntryDetailsLoading}
      error={entryDetailsError && t('errors.supplementDetailsError')}
      onRetry={handleRetryFetchInquiryEntryDetails}
    >
      {activeEntryType === InquiryEntryType.Supplement && <SupplementInquiryDetails />}
      {activeEntryType === InquiryEntryType.Medication && <MedicationInquiryDetails />}
    </Loadable>
  );
};

export default AsideFormCard;
